// OnboardingFlow для admin SPA Editorial.
//
// 7 шагов:
//   1. Welcome — суть, базовые мета-факты (auto-save, audit, ⌘K)
//   2. Dashboard — KPI + recent
//   3. Articles + Block editor (deep dive, 5 bullet'ов)
//   4. Series / Tags / Media Library
//   5. Drafts / Scheduled / Audit
//   6. Settings — profile, editors, language
//   7. Ready — CTA "Create my first article"
//
// Header содержит Stepper (слева), Skip (центр-справа) и LangSwitcher
// (самый правый — глобус). Юзер может переключить язык в любой момент,
// и весь онбординг + основная админка перерендерятся.
//
// Re-runnable: флаг в localStorage `reverium-editorial-admin-onboarding-v1`.
// Сбросить можно через Settings → "Replay onboarding".

import { useEffect, useRef, useState } from "react";
import { useT, useLangStore, type Language } from "../lib/i18n";
import { navigate } from "../App";

const STORAGE_KEY = "reverium-editorial-admin-onboarding-v1";
const TOTAL_STEPS = 7;

export function isOnboardingNeeded(): boolean {
  try {
    return !localStorage.getItem(STORAGE_KEY);
  } catch {
    return false;
  }
}

export function markOnboardingCompleted(): void {
  try {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
  } catch {
    /* ignore */
  }
}

export function resetOnboarding(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

interface Props {
  onClose: () => void;
}

export default function OnboardingFlow({ onClose }: Props) {
  const t = useT();
  const [step, setStep] = useState(1);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        finish();
      } else if (e.key === "ArrowRight") {
        next();
      } else if (e.key === "ArrowLeft") {
        back();
      }
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  function finish() {
    markOnboardingCompleted();
    onClose();
  }

  function next() {
    if (step < TOTAL_STEPS) setStep(step + 1);
    else finish();
  }

  function back() {
    if (step > 1) setStep(step - 1);
  }

  function startCreating() {
    markOnboardingCompleted();
    onClose();
    navigate("/article/new");
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        backdropFilter: "blur(8px) saturate(1.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        animation: "rvm-admin-onb-fade-in 0.25s ease",
      }}
    >
      <div
        style={{
          width: 720,
          maxWidth: "94vw",
          minHeight: 540,
          maxHeight: "92vh",
          background: "var(--bg-1)",
          border: "1px solid var(--border-1)",
          borderRadius: 12,
          boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px 0",
          }}
        >
          <Stepper current={step} total={TOTAL_STEPS} />
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              onClick={finish}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--text-3)",
                fontSize: 12,
                cursor: "pointer",
                fontFamily: "inherit",
                padding: "4px 8px",
                letterSpacing: "0.02em",
                transition: "color 0.15s",
              }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLElement).style.color = "var(--text-0)")
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLElement).style.color = "var(--text-3)")
              }
            >
              {t.onbSkip}
            </button>
            <LangSwitcher />
          </div>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            padding: "32px 44px 24px",
            overflowY: "auto",
          }}
        >
          {step === 1 && (
            <Step
              title={t.onbStep1Title}
              body={t.onbStep1Body}
              bullets={[t.onbStep1Bullet1, t.onbStep1Bullet2, t.onbStep1Bullet3]}
              showLogo
            />
          )}
          {step === 2 && (
            <Step
              title={t.onbStep2Title}
              body={t.onbStep2Body}
              bullets={[t.onbStep2Bullet1, t.onbStep2Bullet2]}
            />
          )}
          {step === 3 && (
            <Step
              title={t.onbStep3Title}
              body={t.onbStep3Body}
              bullets={[
                t.onbStep3Bullet1,
                t.onbStep3Bullet2,
                t.onbStep3Bullet3,
                t.onbStep3Bullet4,
                t.onbStep3Bullet5,
              ]}
            />
          )}
          {step === 4 && (
            <Step
              title={t.onbStep4Title}
              body={t.onbStep4Body}
              bullets={[t.onbStep4Bullet1, t.onbStep4Bullet2, t.onbStep4Bullet3]}
            />
          )}
          {step === 5 && (
            <Step
              title={t.onbStep5Title}
              body={t.onbStep5Body}
              bullets={[t.onbStep5Bullet1, t.onbStep5Bullet2, t.onbStep5Bullet3]}
            />
          )}
          {step === 6 && (
            <Step
              title={t.onbStep6Title}
              body={t.onbStep6Body}
              bullets={[t.onbStep6Bullet1, t.onbStep6Bullet2, t.onbStep6Bullet3]}
            />
          )}
          {step === 7 && <Step title={t.onbStep7Title} body={t.onbStep7Body} bullets={[]} />}
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 22px",
            borderTop: "1px solid var(--border-1)",
            background: "var(--bg-0)",
          }}
        >
          <button
            onClick={back}
            disabled={step === 1}
            style={{
              background: "transparent",
              color: "var(--text-2)",
              border: "1px solid var(--border-1)",
              borderRadius: 8,
              padding: "9px 18px",
              fontSize: 13,
              cursor: step === 1 ? "default" : "pointer",
              fontFamily: "inherit",
              opacity: step === 1 ? 0.3 : 1,
            }}
          >
            {t.onbBack}
          </button>
          {step === TOTAL_STEPS ? (
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={finish}
                style={{
                  background: "transparent",
                  color: "var(--text-1)",
                  border: "1px solid var(--border-1)",
                  borderRadius: 8,
                  padding: "9px 18px",
                  fontSize: 13,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {t.onbStart}
              </button>
              <button
                onClick={startCreating}
                style={{
                  background: "var(--accent, #b8864a)",
                  color: "#1a1a1a",
                  border: "none",
                  borderRadius: 8,
                  padding: "9px 22px",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {t.onbStep7CtaCreate}
              </button>
            </div>
          ) : (
            <button
              onClick={next}
              style={{
                background: "var(--accent, #b8864a)",
                color: "#1a1a1a",
                border: "none",
                borderRadius: 8,
                padding: "9px 22px",
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {t.onbNext}
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes rvm-admin-onb-fade-in {
          from { opacity: 0; transform: scale(0.98); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

// ── Step ──────────────────────────────────────────────────────────────────

function Step({
  title,
  body,
  bullets,
  showLogo,
}: {
  title: string;
  body: string;
  bullets: string[];
  showLogo?: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18, alignItems: showLogo ? "center" : "stretch", textAlign: showLogo ? "center" : "left" }}>
      {showLogo && (
        <img
          src="/r-logo.png"
          alt="Reverium"
          width={88}
          height={88}
          style={{
            opacity: 0.95,
            userSelect: "none",
            pointerEvents: "none",
            filter: "drop-shadow(0 3px 12px rgba(0,0,0,0.4))",
            marginBottom: 4,
          }}
        />
      )}
      <h2
        style={{
          fontFamily: "var(--font-serif, Georgia, 'Iowan Old Style', serif)",
          fontSize: 28,
          fontWeight: 500,
          letterSpacing: "-0.01em",
          color: "var(--text-0)",
          margin: 0,
          lineHeight: 1.15,
        }}
      >
        {title}
      </h2>
      <p
        style={{
          fontSize: 15,
          lineHeight: 1.6,
          color: "var(--text-1)",
          margin: 0,
        }}
      >
        {body}
      </p>
      {bullets.length > 0 && (
        <ul
          style={{
            margin: "4px 0 0",
            paddingLeft: 0,
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: 8,
            textAlign: "left",
            maxWidth: showLogo ? 440 : "none",
            width: "100%",
          }}
        >
          {bullets.map((b, i) => (
            <li
              key={i}
              style={{
                fontSize: 13,
                color: "var(--text-2)",
                lineHeight: 1.55,
                paddingLeft: 18,
                position: "relative",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  left: 0,
                  top: 1,
                  color: "var(--accent, #b8864a)",
                  fontSize: 14,
                }}
              >
                ✶
              </span>
              {b}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Stepper ───────────────────────────────────────────────────────────────

function Stepper({ current, total }: { current: number; total: number }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {Array.from({ length: total }).map((_, i) => {
        const n = i + 1;
        const active = n === current;
        const passed = n < current;
        return (
          <div
            key={i}
            style={{
              width: active ? 22 : 7,
              height: 7,
              borderRadius: 8,
              background:
                passed || active ? "var(--accent, #b8864a)" : "var(--border-1)",
              transition: "width 0.2s, background 0.2s",
            }}
          />
        );
      })}
    </div>
  );
}

// ── LangSwitcher ──────────────────────────────────────────────────────────

function LangSwitcher() {
  const t = useT();
  const lang = useLangStore((s) => s.lang);
  const setLang = useLangStore((s) => s.setLang);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function pick(next: Language) {
    setLang(next);
    setOpen(false);
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        title={t.onbLanguageSwitcherTooltip}
        aria-label={t.onbLanguageSwitcherTooltip}
        style={{
          width: 28,
          height: 28,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: open ? "rgba(255,255,255,0.06)" : "transparent",
          border: "1px solid",
          borderColor: open ? "var(--accent, #b8864a)" : "var(--border-1)",
          borderRadius: 8,
          color: open ? "var(--accent, #b8864a)" : "var(--text-2)",
          cursor: "pointer",
          fontFamily: "inherit",
          transition: "all 0.15s",
        }}
      >
        <GlobeIcon />
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            minWidth: 120,
            background: "var(--bg-1)",
            border: "1px solid var(--border-1)",
            borderRadius: 6,
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
            overflow: "hidden",
            zIndex: 10,
          }}
        >
          <LangOption
            label="English"
            selected={lang === "en"}
            onClick={() => pick("en")}
          />
          <LangOption
            label="Русский"
            selected={lang === "ru"}
            onClick={() => pick("ru")}
          />
        </div>
      )}
    </div>
  );
}

function LangOption({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        padding: "9px 12px",
        background: "transparent",
        border: "none",
        color: selected ? "var(--accent, #b8864a)" : "var(--text-1)",
        fontSize: 13,
        fontWeight: selected ? 500 : 400,
        cursor: "pointer",
        fontFamily: "inherit",
        textAlign: "left",
      }}
      onMouseEnter={(e) =>
        ((e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.04)")
      }
      onMouseLeave={(e) =>
        ((e.currentTarget as HTMLElement).style.background = "transparent")
      }
    >
      <span>{label}</span>
      {selected && <span style={{ fontSize: 12 }}>✓</span>}
    </button>
  );
}

function GlobeIcon() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}
